import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { USE_MOCK_DATA } from '../services/mockData';
import { safeFirebaseQuery } from '../utils/firebaseHelper';
import { EventDetailSEO } from '../components/PageSEO';
import './EventDetail.css';

const mockEvents = {
  'event1': {
    id: 'event1',
    title: 'Italian Masterclass with Chef Marco',
    chef: 'Chef Marco Rossi',
    restaurant: 'Bella Italia',
    date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    time: '19:00',
    location: 'Bella Italia, New York, NY',
    address: '123 Main Street, New York, NY 10001',
    price: 85,
    capacity: 20,
    registered: 12,
    featuredDishes: ['dish1', 'dish5'],
    description: 'Join us for an exclusive Italian dining experience featuring authentic pasta and pizza. Chef Marco will prepare a 5-course meal showcasing traditional Italian techniques passed down through generations.',
    imageUrl: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800',
    menu: ['Antipasto Platter', 'Handmade Pasta', 'Wood-Fired Pizza', 'Tiramisu', 'Wine Pairing'],
    chefBio: 'Chef Marco Rossi brings 20 years of experience from Italy\'s finest restaurants. He specializes in traditional Italian cuisine with a modern twist.',
    includes: ['5-course meal', 'Wine pairing', 'Meet the chef', 'Recipe cards']
  },
  'event2': {
    id: 'event2',
    title: 'Sushi Omakase Experience',
    chef: 'Chef Yuki Tanaka',
    restaurant: 'Sakura Sushi',
    date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000),
    time: '18:30',
    location: 'Sakura Sushi, San Francisco, CA',
    address: '456 Market Street, San Francisco, CA 94102',
    price: 120,
    capacity: 12,
    registered: 8,
    featuredDishes: ['dish3'],
    description: 'An intimate omakase experience with premium ingredients flown in from Japan. Chef Yuki will craft a personalized menu based on the freshest catch of the day.',
    imageUrl: 'https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=800',
    menu: ['Seasonal Sashimi', 'Nigiri Selection', 'Specialty Rolls', 'Miso Soup', 'Green Tea'],
    chefBio: 'Chef Yuki trained in Tokyo for 15 years before bringing authentic Japanese techniques to San Francisco.',
    includes: ['12-piece omakase', 'Sake pairing', 'Chef interaction', 'Take-home menu']
  },
  'event3': {
    id: 'event3',
    title: 'Farm-to-Table Summer Feast',
    chef: 'Chef Sarah Johnson',
    restaurant: 'Garden Fresh',
    date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000),
    time: '18:00',
    location: 'Garden Fresh, Portland, OR',
    address: '789 Farm Road, Portland, OR 97201',
    price: 75,
    capacity: 25,
    registered: 18,
    featuredDishes: ['dish10'],
    description: 'Celebrate summer with a farm-to-table feast featuring locally sourced ingredients. Each dish highlights the season\'s best produce.',
    imageUrl: 'https://images.unsplash.com/photo-1546793665-c74683f339c1?w=800',
    menu: ['Seasonal Salad', 'Grilled Vegetables', 'Herb-Crusted Fish', 'Berry Tart', 'Local Wine'],
    chefBio: 'Chef Sarah is passionate about sustainable, local ingredients and has been featured in multiple food publications.',
    includes: ['4-course meal', 'Local wine', 'Farm tour', 'Recipe collection']
  }
};

function EventDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  const fetchEvent = useCallback(async () => {
    try {
      const eventData = await safeFirebaseQuery(
        async () => {
          const docRef = doc(db, 'supperClubEvents', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
          }
          return null;
        },
        async () => {
          return mockEvents[id] || null;
        },
        'Error fetching event'
      );

      if (eventData) {
        setEvent(eventData);
      } else {
        navigate('/supperclub');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      // Try mock data
      if (mockEvents[id]) {
        setEvent(mockEvents[id]);
      } else {
        navigate('/supperclub');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const checkRegistration = useCallback(async () => {
    if (!user) return;

    try {
      if (USE_MOCK_DATA) {
        const registrations = JSON.parse(localStorage.getItem('eventRegistrations') || '[]');
        const registered = registrations.some(r => r.eventId === id && r.userId === user.uid);
        setIsRegistered(registered);
        return;
      }

      const q = query(
        collection(db, 'eventRegistrations'),
        where('eventId', '==', id),
        where('userId', '==', user.uid)
      );
      const querySnapshot = await getDocs(q);
      setIsRegistered(!querySnapshot.empty);
    } catch (error) {
      console.error('Error checking registration:', error);
    }
  }, [user, id]);

  useEffect(() => {
    fetchEvent();
    checkRegistration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const handleRegister = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (isRegistered) {
      return;
    }

    setRegistering(true);
    try {
      if (USE_MOCK_DATA) {
        const registrations = JSON.parse(localStorage.getItem('eventRegistrations') || '[]');
        registrations.push({
          eventId: id,
          userId: user.uid,
          registeredAt: new Date().toISOString()
        });
        localStorage.setItem('eventRegistrations', JSON.stringify(registrations));
        setIsRegistered(true);
        setShowPayment(false);
        alert('Registration successful! (Mock mode)');
        setRegistering(false);
        return;
      }

      // In production, this would integrate with a payment system
      await addDoc(collection(db, 'eventRegistrations'), {
        eventId: id,
        userId: user.uid,
        userEmail: user.email,
        registeredAt: new Date(),
        status: 'confirmed'
      });

      setIsRegistered(true);
      setShowPayment(false);
      alert('Registration successful! Check your email for confirmation and calendar invite.');
    } catch (error) {
      console.error('Error registering:', error);
      alert('Error registering. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  const handleAddToCalendar = () => {
    if (!event) return;

    const startDate = new Date(event.date);
    const [hours, minutes] = event.time.split(':');
    startDate.setHours(parseInt(hours), parseInt(minutes));
    const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000); // 3 hours

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.address || event.location)}`;

    window.open(calendarUrl, '_blank');
  };

  const formatDate = (date) => {
    const d = date instanceof Date ? date : date.toDate();
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const spotsLeft = event ? event.capacity - (event.registered || 0) : 0;

  if (loading) {
    return (
      <div className="event-detail-container">
        <div className="loading">Loading event details...</div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <>
      {event && <EventDetailSEO event={event} />}
      <div className="event-detail-container">
        <button onClick={() => navigate('/supperclub')} className="btn-back">
          ← Back to Events
        </button>

        <div className="event-detail-hero">
          <div className="event-hero-image">
            {event.imageUrl ? (
              <img src={event.imageUrl} alt={event.title} />
            ) : (
              <div className="event-hero-placeholder">🍽️</div>
            )}
          </div>
          <div className="event-hero-content">
            <h1>{event.title}</h1>
            <div className="event-hero-meta">
              <p className="event-chef-large">👨‍🍳 {event.chef}</p>
              <p className="event-restaurant-large">📍 {event.restaurant}</p>
            </div>
            <div className="event-hero-details">
              <div className="hero-detail">
                <span className="hero-icon">📅</span>
                <div>
                  <strong>{formatDate(event.date)}</strong>
                  <span className="hero-time">at {event.time}</span>
                </div>
              </div>
              <div className="hero-detail">
                <span className="hero-icon">📍</span>
                <div>
                  <strong>{event.location}</strong>
                  {event.address && <span className="hero-address">{event.address}</span>}
                </div>
              </div>
              <div className="hero-detail">
                <span className="hero-icon">💰</span>
                <div>
                  <strong>${event.price}</strong>
                  <span className="hero-price">per person</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="event-detail-sections">
          <div className="event-description-section">
            <h2>About This Event</h2>
            <p className="event-description-text">{event.description}</p>

            {event.chefBio && (
              <div className="chef-bio">
                <h3>About {event.chef}</h3>
                <p>{event.chefBio}</p>
              </div>
            )}
          </div>

          <div className="event-menu-section">
            <h2>Menu</h2>
            <ul className="menu-list">
              {event.menu?.map((item, index) => (
                <li key={index} className="menu-item">
                  <span className="menu-icon">🍽️</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {event.includes && (
            <div className="event-includes-section">
              <h2>What's Included</h2>
              <ul className="includes-list">
                {event.includes.map((item, index) => (
                  <li key={index} className="include-item">
                    <span className="check-icon">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="event-registration-section">
            <div className="registration-card">
              <h2>Join This Event</h2>
              <div className="registration-info">
                <div className="capacity-info">
                  <span>{event.registered || 0} / {event.capacity} registered</span>
                  {spotsLeft > 0 && (
                    <span className="spots-available">{spotsLeft} spots available</span>
                  )}
                  {spotsLeft === 0 && (
                    <span className="spots-sold-out">Sold Out</span>
                  )}
                </div>

                {!isRegistered && spotsLeft > 0 && (
                  <>
                    {!showPayment ? (
                      <div className="registration-actions">
                        <button
                          onClick={() => setShowPayment(true)}
                          className="btn-register"
                        >
                          Register Now - ${event.price}
                        </button>
                        <button
                          onClick={handleAddToCalendar}
                          className="btn-calendar"
                        >
                          📅 Add to Calendar
                        </button>
                      </div>
                    ) : (
                      <div className="payment-section">
                        <p className="payment-note">
                          In production, this would integrate with a payment processor like Stripe.
                          For now, registration is free in demo mode.
                        </p>
                        <div className="payment-actions">
                          <button
                            onClick={() => setShowPayment(false)}
                            className="btn-cancel-payment"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleRegister}
                            disabled={registering}
                            className="btn-confirm-payment"
                          >
                            {registering ? 'Processing...' : 'Confirm Registration'}
                          </button>
                          <p className="affiliate-note">
                            * Registration includes affiliate commission. This helps support RatedBite.
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {isRegistered && (
                  <div className="registered-status">
                    <div className="success-icon">✓</div>
                    <h3>You're Registered!</h3>
                    <p>Check your email for confirmation and event details.</p>
                    <button
                      onClick={handleAddToCalendar}
                      className="btn-calendar"
                    >
                      📅 Add to Calendar
                    </button>
                  </div>
                )}

                {spotsLeft === 0 && !isRegistered && (
                  <div className="sold-out-status">
                    <p>This event is sold out. Join the waitlist to be notified if spots become available.</p>
                    <button className="btn-waitlist">Join Waitlist</button>
                  </div>
                )}

                {!user && (
                  <div className="login-prompt-event">
                    <p>Please <a href="/login">login</a> or <a href="/signup">sign up</a> to register for this event.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EventDetail;

